import {
  FGLink,
  FGCard,
  FGTypography,
  FGGridItem,
  FGFooter,
  FGGridContainer,
  FGLayout,
  FGSection,
  FGStack,
  FGAvatar,
} from '@fg/design-system';
import { useEffect } from 'react';
import { BroadcastEventType } from '../services/broadcast/broadcastHelper';
import { BroadcastService } from '../services/broadcast/broadcastService';
import { Header } from './Header';
import { getBannerMessage, MessageType } from '../services/bannerMessageHelper';

type Props = {
  broadcastService: BroadcastService;
  customProps: {
    clients: {
      appUrl: string;
      appName: string;
      description: string;
      iconUrl: string;
    }[];
    errorMessageCode?: string;
    fullName: string;
  };
};

export const Launchpad = ({ broadcastService, customProps }: Props) => {
  const { clients, errorMessageCode, fullName } = customProps;
  let errorMessage = getBannerMessage(errorMessageCode, MessageType.LaunchpadError);

  const apps = clients.map((client) => ({
    name: client.appName,
    description: client.description,
    iconUrl: client.iconUrl,
    url: client.appUrl,
  }));

  useEffect(() => {
    broadcastService.postEvent({ eventType: BroadcastEventType.LAUNCHPAD_PAGE_RENDERED });
  }, []);

  return (
    <FGLayout
      header={<Header logoType="" fullName={fullName} />}
      footer={
        <FGFooter>
          <FGGridContainer containerPadding={24} isFluid>
            <FGGridItem xxs={12} xs={11} classNames="fg-as--fe">
              <p className="fg-body3-long">
                Copyright © 2025 Fitch Solutions, Inc., Fitch Ratings, Inc., and their subsidiaries.
              </p>
            </FGGridItem>
            <FGGridItem xxs={12} xs={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <p className="fg-body3-long">
              <FGLink onClick={() => window.open('https://www.fitch.group/policies/')} appearance="blue">
                  Policies
                  </FGLink>
              </p>
            </FGGridItem>
          </FGGridContainer>
        </FGFooter>
      }
    >
      {errorMessage && (
        <div>
          <FGSection padding="lg" horizontalAlign="center">
            <FGTypography tag="h6" style="body2">
              {errorMessage}
            </FGTypography>
            <FGLink appearance="default" onClick={() => window.open(`${window.location.origin}/login`)}>
              Return to Login Page
            </FGLink>
          </FGSection>
        </div>
      )}
      {!errorMessage && apps.length > 0 && (
        <>
          <FGSection padding="lg" horizontalAlign="center">
            <FGTypography tag="h1" style="headline3" color="strong" alignment="center">
              Here are all the Fitch applications you are entitled to.
            </FGTypography>
            <FGTypography tag="h1" style="headline3" color="strong" alignment="center">
              Where do you want to go?
            </FGTypography>
          </FGSection>
          {apps.length > 2 ? (
            <FGGridContainer xxs={1} xs={2} md={3} classNames="fg-mg--b6" style={{ maxWidth: '1100px' }}>
              {apps.map((app, index) => (
                <FGStack key={index}>
                  <FGLink
                    href={app.url}
                    className="fg-h--full fg-w--full pi-launchpad-app"
                    style={{ textDecoration: 'none' }}
                  >
                    <FGCard hasBorder fullHeight>
                      <FGStack direction="column">
                        <FGStack alignItems="center">
                          <FGAvatar size="xlarge" isSquare isBordered>
                            <img src={app.iconUrl} alt={app.name + ' Logo'} width="69" height="69" />
                          </FGAvatar>
                          <FGTypography id="pi-launchpad-app-name" tag="span" style="headline6" color="strong">
                            {app.name}
                          </FGTypography>
                        </FGStack>
                        <FGTypography tag="p" style="body4" color="weak">
                          {app.description}
                        </FGTypography>
                      </FGStack>
                    </FGCard>
                  </FGLink>
                </FGStack>
              ))}
            </FGGridContainer>
          ) : (
            <FGGridContainer xxs={1} xs={2} classNames="fg-mg--b6 pi-launchpad-app" style={{ maxWidth: '750px' }}>
              {apps.map((app, index) => (
                <FGStack key={index}>
                  <FGLink href={app.url} className="fg-h--full fg-w--full" style={{ textDecoration: 'none' }}>
                    <FGCard hasBorder fullHeight>
                      <FGStack direction="column">
                        <FGStack alignItems="center">
                          <FGAvatar size="xlarge" isSquare isBordered>
                            <img src={app.iconUrl} alt={app.name + ' Logo'} width="69" height="69" />
                          </FGAvatar>
                          <FGTypography id="pi-launchpad-app-name" tag="span" style="headline6" color="strong">
                            {app.name}
                          </FGTypography>
                        </FGStack>
                        <FGTypography tag="p" style="body4" color="weak">
                          {app.description}
                        </FGTypography>
                      </FGStack>
                    </FGCard>
                  </FGLink>
                </FGStack>
              ))}
            </FGGridContainer>
          )}
        </>
      )}
    </FGLayout>
  );
};
